// src/components/modals/confirmDialog.ts
//
// The one way to ask "are you sure?" (plan 2026-10-08-one-modal-component, §2).
// A thin layer over `modals.openConfirmModal` that adds the two things every
// call site used to have to remember, and some forgot:
//
// 1. The confirm button's colour is REQUIRED. Mantine's ConfirmModal sets no
//    colour of its own, so a call that forgets `confirmProps.color` renders a
//    delete button in the neutral primary colour. Making it a required field
//    turns that slip into a type error.
//
// 2. The layer. The ModalsProvider renders every confirm at Mantine's default
//    elevation + 1 (201) — from OUTSIDE the caller's tree, so it cannot see a
//    raised layer the caller sits in. A confirm opened from the authorization
//    drawer (300) therefore opened behind the drawer. Pass the layer and the
//    confirm matches it (ADR-0124: match, never exceed).
//
//    Pass `zIndex` ONLY when there is one. ModalsProvider spreads the call's
//    props over its own `zIndex: 201`, so a present-but-undefined key would
//    knock it down to Mantine's plain 200.
//
// Use the hook inside a component. Use the function where a hook cannot go — an
// exported `openDeleteReportModal(...)` helper, or a surface that OWNS a raised
// layer and so must name its own `zIndex` (the hook reads the layer the
// component is rendered in, not the one it renders).

import type { ReactNode } from "react";
import type { MantineColor } from "@mantine/core";
import { modals } from "@mantine/modals";

type OpenConfirmModal = Parameters<typeof modals.openConfirmModal>[0];
type ButtonPropsOf<K extends "confirmProps" | "cancelProps"> = NonNullable<OpenConfirmModal[K]>;

import { useModalLayerZIndex } from "./ModalLayer";

export type ConfirmDialogOptions = {
  title: ReactNode;
  children?: ReactNode;
  labels?: { confirm: ReactNode; cancel: ReactNode };
  /** Required — see note 1. `"red"` for anything destructive. */
  confirmColor: MantineColor;
  /** Other confirm-button props (`loading`, `data-*`); colour goes in `confirmColor`. */
  confirmProps?: Omit<ButtonPropsOf<"confirmProps">, "color">;
  cancelProps?: ButtonPropsOf<"cancelProps">;
  onConfirm?: () => void;
  onCancel?: () => void;
  closeOnConfirm?: boolean;
  closeOnClickOutside?: boolean;
  /** Defaults to `true` for consistency with the rest of the app's modals. */
  centered?: boolean;
};

/** Opens a confirm dialog; returns its modal id. */
export function confirmDialog(
  { confirmColor, confirmProps, centered = true, ...options }: ConfirmDialogOptions,
  { zIndex }: { zIndex?: number } = {},
): string {
  return modals.openConfirmModal({
    ...options,
    centered,
    confirmProps: { ...confirmProps, color: confirmColor },
    ...(zIndex === undefined ? {} : { zIndex }),
  });
}

/** `confirmDialog`, matched to the modal layer the calling component sits in. */
export function useConfirmDialog(): (options: ConfirmDialogOptions) => string {
  const zIndex = useModalLayerZIndex();
  return (options) => confirmDialog(options, { zIndex });
}
